import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import { Outlet } from "@tanstack/react-router";
import { useMemo } from "react";
import { IntlProvider } from "use-intl";

import { AdvertisingConsentBanner } from "@/components/AdvertisingConsentBanner";
import { FloatingVideoPlayer } from "@/components/floating-video-player";
import { GlobalGuestConversion } from "@/components/global-guest-conversion";
import { TopLoader } from "@/components/top-loader";
import { Toaster } from "@/components/ui/sonner";
import { PwaInstallProvider } from "@/contexts/PwaInstallProvider";
import { VideoPlayerProvider } from "@/contexts/video-player-context";
import { FeaturePageDocumentStructuredData } from "@/features/feature-page/FeaturePageDocumentStructuredData";
import { useBrowserTimeZone } from "@/hooks/use-browser-time-zone";
import { HashModalProvider } from "@/hooks/use-hash-modal";
import { usePersistAnonymousRouteLocale } from "@/hooks/use-persist-anonymous-route-locale";
import { useWebPushRecovery } from "@/hooks/useWebPushRecovery";
import type { Messages } from "@/i18n/messages";
import { rememberHydratedMessages } from "@/i18n/route-message-cache";
import type { SessionUserSnapshot } from "@/lib/auth-server-fns";
import type { ViewerLocale } from "@garimpa/lib/viewerPreferences";

import { RootAnalyticsTracking } from "./-RootAnalyticsTracking";
import { RootDocument } from "./-RootDocument";
import { RouteTrackingScope } from "./-RouteTrackingScope";

interface RootAppProps {
  initialNow: string;
  initialTimeZone: string;
  locale: ViewerLocale;
  messages: Messages;
  queryClient: QueryClient;
  routeLocale: ViewerLocale | null;
  user: SessionUserSnapshot | null;
  viewerLocale: ViewerLocale;
}

export function RootApp({
  initialNow,
  initialTimeZone,
  locale,
  messages,
  queryClient,
  routeLocale,
  user,
  viewerLocale,
}: Readonly<RootAppProps>) {
  useWebPushRecovery();
  rememberHydratedMessages(locale, messages);
  usePersistAnonymousRouteLocale({ queryClient, routeLocale, user, viewerLocale });
  const timeZone = useBrowserTimeZone({ queryClient, initialTimeZone });
  const now = useMemo(() => new Date(initialNow), [initialNow]);

  return (
    <RootDocument lang={locale}>
      <QueryClientProvider client={queryClient}>
        <RootAnalyticsTracking isAdmin={user?.role === "admin"} userId={user?.id ?? null} />
        <TopLoader />
        <IntlProvider locale={locale} messages={messages} timeZone={timeZone} now={now}>
          <RouteTrackingScope>
            <PwaInstallProvider>
              <VideoPlayerProvider>
                <HashModalProvider>
                  <GlobalGuestConversion />
                  {user?.role !== "admin" && <AdvertisingConsentBanner />}
                  <Outlet />
                  <FloatingVideoPlayer />
                </HashModalProvider>
              </VideoPlayerProvider>
            </PwaInstallProvider>
          </RouteTrackingScope>
        </IntlProvider>
        <Toaster richColors />
        <FeaturePageDocumentStructuredData />
      </QueryClientProvider>
    </RootDocument>
  );
}
